import React from "react";
// Lucide Icons
import {
  BookOpen,
  Lightbulb,
  Sparkles,
  Rabbit,
  Turtle,
  Handshake,
  CornerDownRight,
  Zap,
  Star,
} from "lucide-react";

// Image Imports (Keep these as you had them)
import fox from "../../Photos/grapes.png";
import lion from "../../Photos/lionmouse.png";
import ant from "../../Photos/Antsandgrass.png";
import rabbit from "../../Photos/rabbit.png";
import sticks from "../../Photos/sticks.png";
import { Link } from "react-router-dom";

import {MoralStoriesData} from "./MoralStoriesData";

// --- EXPANDED DATA WITH FULL STORY CONTENT ---
// const MoralStoriesData = [
//   {
//     id: 1,
//     title: "🦊 The Fox and the Grapes",
//     icon: <CornerDownRight className="w-6 h-6 text-pink-600" />,
//     img: fox,
//     summary:
//       "A clever fox tried to reach a bunch of juicy grapes, but when he failed, he pretended they were sour.",
//     moral: "Don't say something is bad just because you can't have it.",
//     fullStory:
//       "A Fox saw some grapes hanging from a vine and wished to eat them. He jumped and leaped with all his might, but the grapes were too high to reach. Finally, the Fox stopped trying, turned up his nose, and said, 'I didn't want those anyway! They are sour and not fit to eat.' He walked away with his nose in the air.",
//   },
//   {
//     id: 2,
//     title: "🦁 The Lion and the Mouse",
//     icon: <Handshake className="w-6 h-6 text-blue-600" />,
//     img: lion,
//     summary:
//       "A tiny mouse promises to help a mighty lion one day. Later, he frees the lion from a hunter’s net.",
//     moral: "No act of kindness is ever too small.",
//     fullStory:
//       "A Lion was asleep when a little Mouse started running up and down his mane. This woke the Lion, who angrily grabbed the Mouse and prepared to eat him. 'Please, King of the Jungle!' cried the Mouse. 'Forgive me this time! I promise, I will repay you!' The Lion laughed at the idea of a Mouse helping a Lion, but let him go. Later, the Lion was caught in a hunter's strong net. The Mouse, hearing the Lion's roar, ran to the net and quickly chewed the ropes, setting the mighty Lion free. ",
//   },
//   {
//     id: 3,
//     title: "🐜 The Ant and the Grasshopper",
//     icon: <Sparkles className="w-6 h-6 text-purple-600" />,
//     img: ant,
//     summary:
//       "The hardworking Ant stored food for winter while the Grasshopper only sang. Winter came, and the Grasshopper had nothing to eat.",
//     moral: "Hard work and preparation keep you safe in the future.",
//     fullStory:
//       "All summer long, an Ant worked diligently, storing food for the coming winter. A Grasshopper spent his days singing and laughing at the Ant. 'Why do you work so hard?' the Grasshopper asked. 'Enjoy the sunshine!' When winter arrived, the Ant had plenty of food in his warm home. The Grasshopper, cold and starving, went to the Ant's door to beg for a meal, but the Ant turned him away. ",
//   },
//   {
//     id: 4,
//     title: "🐢 The Tortoise and the Hare",
//     icon: <Turtle className="w-6 h-6 text-green-600" />,
//     img: rabbit,
//     summary:
//       "The fast Hare took a nap during a race, letting the slow but steady Tortoise win.",
//     moral: "Slow and steady wins the race.",
//     fullStory:
//       "A speedy Hare made fun of a slow Tortoise, challenging him to a race. The Tortoise accepted. When the race began, the Hare ran far ahead. Feeling so confident he couldn't lose, the Hare decided to take a short nap. The Tortoise, never stopping and walking slowly but steadily, passed the sleeping Hare and crossed the finish line first.",
//   },
//   {
//     id: 5,
//     title: "🌳 The Bundle of Sticks",
//     icon: <Rabbit className="w-6 h-6 text-yellow-600" />,
//     img: sticks,
//     summary:
//       "A father showed his sons that sticks are weak alone but strong together.",
//     moral: "We are stronger when we stay united.",
//     fullStory:
//       "A father had many sons who were always fighting and arguing. He gave them a bundle of sticks and asked them to break it. They all tried, but none could break the whole bundle. The father then untied the bundle and asked them to break the sticks one by one. They did this easily. The father said, 'My sons, when you fight, you are like the single sticks—easy to break. But when you are united, you are like the bundle—impossible to break.'",
//   },
// ];
// -------------------------------------------------------------
// --- COMPONENT LOGIC STARTS HERE ---
// -------------------------------------------------------------

// Placeholder function to simulate navigation (replace with actual router logic)
const onStorySelect = (storyId) => {
  // In a real app, this would be: navigate(`/story/${storyId}`);
  console.log(`Navigating to story details for ID: ${storyId}`);

  // For demonstration, we'll temporarily show the details in an alert
  const selectedStory = MoralStoriesData.find((s) => s.id === storyId);
  if (selectedStory) {
    alert(
      `Reading Story: ${selectedStory.title}\n\n${selectedStory.fullStory}`
    );
  }
};

const MoralStoriesPage = () => {
  return (
    <main className="min-h-screen w-full bg-gradient-to-br from-blue-100/50 via-pink-100/50 to-yellow-100/50 font-sans">
      {/* HERO SECTION: Very Bright and Playful Header */}
      <header className="w-full pt-16 pb-12 flex flex-col items-center justify-center text-center bg-gradient-to-br from-sky-400 to-pink-500 shadow-2xl shadow-sky-500/50">
        <h1 className="text-7xl md:text-9xl font-black text-white drop-shadow-xl [text-shadow:_0_6px_6px_rgb(0_0_0_/_0.2)]">
          MAGICAL STORIES!
        </h1>
        <p className="text-2xl md:text-4xl text-white/95 font-extrabold mt-4 tracking-wider">
          Fun, Friendship & Big Lessons!
        </p>
      </header>

      {/* STORIES GRID: Full-Width, Fun Spacing */}
      <section className="w-full p-8 py-16 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-12 bg-white/70 backdrop-blur-sm shadow-inner">
        {MoralStoriesData.map((story) => (
          <article
            key={story.id}
            className="bg-white rounded-[32px] shadow-2xl p-6 border-4 border-white transition-all duration-300 ease-in-out
                       hover:border-pink-500 hover:shadow-pink-400/70 hover:-translate-y-2 group"
            aria-labelledby={`story-title-${story.id}`}
          >
            {/* IMAGE */}
            <div className="w-full h-64 md:h-72 lg:h-80 rounded-[24px] overflow-hidden shadow-lg mb-6 border-4 border-pink-200">
              <img
                src={story.img}
                alt={`Illustration for ${story.title.replace(
                  /[^a-zA-Z\s]/g,
                  ""
                )}`}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                loading="lazy"
              />
            </div>

            {/* TITLE & ICON */}
            <h2
              id={`story-title-${story.id}`}
              className="flex items-center text-3xl font-extrabold text-gray-800 mb-4 border-b-4 border-dashed border-purple-200 pb-3"
            >
              {story.icon}
              <span className="ml-3">
                {story.title.replace(/[^a-zA-Z\s]/g, "")}
              </span>
            </h2>

            {/* SUMMARY (Uses Lucide Icon) */}
            <div className="flex items-start gap-3 text-gray-700 text-lg leading-relaxed mb-4">
              <CornerDownRight className="w-6 h-6 mt-1 text-pink-500 flex-shrink-0" />
              <p>
                <span className="font-extrabold text-pink-600">The Tale: </span>
                {story.summary}
              </p>
            </div>

            {/* MORAL (Uses Lucide Icon) */}
            <div className="bg-green-100 p-4 rounded-3xl mt-5 shadow-inner border border-green-300 flex items-start gap-3">
              <Lightbulb className="w-7 h-7 text-green-700 flex-shrink-0 mt-0.5" />
              <span className="text-xl font-black text-green-800">
                Lesson: {story.moral}
              </span>
            </div>

            {/* BUTTON: Modified to trigger navigation/selection */}
            <Link
              to={`/moralstories/${story.id}`} // <-- Navigate to story page
              className="mt-6 w-full py-3 bg-purple-500 text-white font-black rounded-full 
             shadow-xl shadow-purple-300/80 hover:bg-purple-600 active:scale-[0.95] 
             transition-all duration-200 flex items-center justify-center gap-2 text-xl"
              aria-label={`Read the full story of ${story.title.replace(
                /[^a-zA-Z\s]/g,
                ""
              )}`}
            >
              <BookOpen className="w-6 h-6" />
              Read Full Story!
            </Link>
          </article>
        ))}
      </section>
    </main>
  );
};

export default MoralStoriesPage;
